import React, { Suspense, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate } from "react-router-dom";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security, LoginCallback } from "@okta/okta-react";

import "./App.css";
import { DashboardPage } from "./pages/Dashboard/Dashboard.page";
import { OrganizationPage } from "./pages/Organization/Organization.page";
import { AttributionPage } from "./pages/Attribution/Attribution.page";
import { AssociateAttributionsPage } from "./pages/AssociateAttributions/AssociateAttributions.page";
import { BundlePage } from "./pages/Bundle/Bundle.page";
import { LinkPage } from "./pages/Link/Link.page";

import Login from "./pages/login";
import { ApiServiceContextProvider } from "./api/ApiService.context";
import { AuthContextProvider } from "./api/Auth.context";
import { AppHeader } from "./components/Header/Header";

import getOktaConfig from "./oktaConfig";
import AuthRequiredModal from "./components/AuthRequiredModal/AuthRequiredModal";
import CorsErrorModal from "./components/CorsErrorModal/CorsErrorModal";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import { useAuthEvents } from "./useAuthEvents";
import { QuoteExperiencePageQuickSetup } from "./pages/QuoteExperience/QuoteExperienceQuickSetup.page";
import { DataPage } from "./pages/Data/Data.page";


const AuthHandler: React.FC = () => {
  // Initialize Okta Authentication
  const oktaConfig = getOktaConfig();
  const oktaAuth = new OktaAuth(oktaConfig['oidc']);
  const navigate = useNavigate();
  const [corsErrorModalOpen, setCorsErrorModalOpen] = useState(false);
  const [authRequiredModalOpen, setAuthRequiredModalOpen] = useState(false);


  const triggerLogin = () => {
    navigate("/login");
  };

  const restoreOriginalUri = async (_oktaAuth: any, originalUri?: string) => {
    navigate(toRelativeUrl(originalUri || "/", window.location.origin));
  };

  const customAuthHandler = async () => {
    const previousAuthState = oktaAuth.authStateManager.getPreviousAuthState();
    if (!previousAuthState || !previousAuthState.isAuthenticated) {
      triggerLogin();
    } else {
      setAuthRequiredModalOpen(true);
    }
  };

  return (
    <Security
      oktaAuth={oktaAuth}
      onAuthRequired={customAuthHandler}
      restoreOriginalUri={restoreOriginalUri}
    >
      <AppRoutes {...{ corsErrorModalOpen, setCorsErrorModalOpen, authRequiredModalOpen, setAuthRequiredModalOpen, triggerLogin }} />
    </Security>
  );
};

const AppRoutes: React.FC<{
  corsErrorModalOpen: boolean;
  setCorsErrorModalOpen: (open: boolean) => void;
  authRequiredModalOpen: boolean;
  setAuthRequiredModalOpen: (open: boolean) => void;
  triggerLogin: () => void;
}> = ({ corsErrorModalOpen, setCorsErrorModalOpen, authRequiredModalOpen, setAuthRequiredModalOpen, triggerLogin }) => {

  useAuthEvents();
  return (
    <div className="app">
      <AppHeader {...{ setCorsErrorModalOpen }} />
      <CorsErrorModal {...{ corsErrorModalOpen, setCorsErrorModalOpen }} />
      <AuthRequiredModal {...{ authRequiredModalOpen, setAuthRequiredModalOpen, triggerLogin }} />

      <div className="app-main-panel">
        <AuthContextProvider>
          <ApiServiceContextProvider mocked={false}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/login/callback" element={<LoginCallback />} />

              {/* Protected Routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/organization" element={<OrganizationPage />} />
                <Route path="/organization/:organizationGuid" element={<OrganizationPage />} />
                <Route path="/organization/:organizationGuid/attributions" element={<AssociateAttributionsPage />} />
                <Route path="/organization/:organizationGuid/bundles" element={<BundlePage />} />
                <Route path="/organization/:organizationGuid/bundles/:bundleGuid" element={<BundlePage />} />
                <Route path="/organization/:organizationGuid/quote-experience" element={<QuoteExperiencePageQuickSetup />} />
                <Route path="/organization/:organizationGuid/quote-experience/:quoteExperienceBundleGuid" element={<QuoteExperiencePageQuickSetup />} />
                <Route path="/organization/:organizationGuid/links" element={<LinkPage />} />
                <Route path="/organization/:organizationGuid/links/:linkGuid" element={<LinkPage />} />
                <Route path="/attribution" element={<AttributionPage />} />
                <Route path="/attribution/:attributionGuid" element={<AttributionPage />} />
                <Route path="/data" element={<DataPage />} />
                {/* Redirects any unrecognized route to the base path */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </ApiServiceContextProvider>
        </AuthContextProvider>
      </div>
    </div>
  );
};

function App() {
  return (
    <Suspense fallback={<div />}>
      <Router>
        <AuthHandler />
      </Router>
    </Suspense>
  );
}

export default App;
